import { useEffect } from 'react'

import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from 'react-query'

import Actioncable from 'app/services/Actioncable'
import {
  getAuctionWatchLists,
  getBuyerAuctions,
  getBuyerBids,
  getLastBids,
  getSellerAuctions,
  getSellerBids
} from 'app/services/Api/auction'
import Deserializer from 'app/services/Deserializer'
import { hasChannelUnsubscribe, isOfType } from 'app/utils/helpers/type.helpers'

import { auctionKeys } from './auction.keys'

export const auctionConfig = {
  getLastBids: {
    key: auctionKeys.getLastBids,
    request: async ({ queryKey }) => {
      const [_key, _, auctionId] = queryKey

      const resp = await getLastBids(auctionId)

      return resp
    }
  },
  getBuyerAuctions: {
    key: auctionKeys.getBuyerAuctions,
    request: async ({ queryKey }) => {
      const [_key, _, params] = queryKey
      const resp = await getBuyerAuctions(params)

      const auctions = await Deserializer.deepDeserialize(resp.auctions)

      return { auctions, pagy: resp.pagy, items_count: resp.items_count }
    }
  },
  getSellerAuctions: {
    key: auctionKeys.getSellerAuctions,
    request: async ({ queryKey }) => {
      const [_key, _, params] = queryKey
      const resp = await getSellerAuctions(params)

      const auctions = await Deserializer.deepDeserialize(resp.auctions)

      return { auctions, pagy: resp.pagy, items_count: resp.items_count }
    }
  },
  getBuyerBids: {
    key: auctionKeys.getBuyerBids,
    request: async ({ queryKey }) => {
      const [_key, _, params] = queryKey

      const resp = await getBuyerBids(params)

      return resp
    }
  },
  getSellerBids: {
    key: auctionKeys.getSellerBids,
    request: async ({ queryKey }) => {
      const [_key, _, params] = queryKey

      const resp = await getSellerBids(params)

      return resp
    }
  },
  getAuctionWatchLists: {
    key: auctionKeys.getAuctionWatchLists,
    request: async ({ queryKey }) => {
      const [_key, _, params] = queryKey

      const resp = await getAuctionWatchLists(params)

      const watchlist = await Deserializer.deepDeserialize(resp.watchlist)

      return { watchlist, pagy: resp.pagy, items_count: resp.items_count }
    }
  },
  getSellerInfiniteAuctions: {
    key: auctionKeys.getSellerAuctions(),
    request: async (queryParams) => {
      const { pageParam } = queryParams

      if (!isOfType.null(pageParam)) {
        const resp = await getSellerAuctions({
          page: pageParam || 1
        })

        const auctions = await Deserializer.deepDeserialize(resp.auctions)

        return { auctions, pagy: resp.pagy, items_count: resp.items_count }
      }
      return { auctions: [], pagy: {}, items_count: 0 }
    }
  },
  getAuctionInfiniteWatchLists: {
    key: auctionKeys.getAuctionWatchLists(),
    request: async (queryParams) => {
      const { pageParam } = queryParams

      if (!isOfType.null(pageParam)) {
        const resp = await getAuctionWatchLists({
          page: pageParam || 1
        })

        const watchlist = await Deserializer.deepDeserialize(resp.watchlist)

        return { watchlist, pagy: resp.pagy, items_count: resp.items_count }
      }
      return { watchlist: [], pagy: {}, items_count: 0 }
    }
  },
  getBuyerInfiniteAuctions: {
    key: auctionKeys.getBuyerAuctions(),
    request: async (queryParams) => {
      const { pageParam } = queryParams

      if (!isOfType.null(pageParam)) {
        const resp = await getBuyerAuctions({
          page: pageParam || 1
        })

        const auctions = await Deserializer.deepDeserialize(resp.auctions)

        return { auctions, pagy: resp.pagy, items_count: resp.items_count }
      }
      return { auctions: [], pagy: {}, items_count: 0 }
    }
  }
}

interface UseGetListing {
  auctionId: number
  options?: UseQueryOptions<PublicBidHistory>
}

export const useGetLastBids = (props: UseGetListing) => {
  const { auctionId, options } = props
  const { getLastBids: config } = auctionConfig

  const state = useQuery<PublicBidHistory>(
    config.key(auctionId),
    config.request,
    {
      ...options
    }
  )

  const queryClient = useQueryClient()
  const isSubscribe = !!auctionId && options?.enabled

  useEffect(() => {
    let channel: unknown = null

    if (auctionId && !channel && isSubscribe) {
      Actioncable.subscribe(
        { channel: 'BidsNotificationsChannel', auction_id: auctionId },
        {
          received: async (data) => {
            await queryClient.setQueryData(
              config.key(auctionId),
              // @ts-ignore
              (oldData: PublicBidHistory) => {
                if (!!oldData?.bids.length) {
                  const { bids } = oldData

                  if (bids.length < 5) {
                    return { bids: [data, ...bids] }
                  }

                  const bidsWithoutLast = bids.slice(0, -1)

                  return { bids: [data, ...bidsWithoutLast] }
                }

                return oldData
              }
            )
          }
        }
      ).then((c) => {
        channel = c
      })
    }

    return (): void => {
      if (hasChannelUnsubscribe(channel)) channel?.unsubscribe()
    }
  }, [isSubscribe])

  return state
}

interface UseGetBuyerAuctions {
  params?: PaginationParams
  options?: UseQueryOptions<PaginationAuctionModel>
}

export const useGetBuyerAuctions = (props: UseGetBuyerAuctions) => {
  const { options, params } = props
  const { getBuyerAuctions: config } = auctionConfig

  const state = useQuery<PaginationAuctionModel>(
    config.key(params),
    config.request,
    {
      ...options
    }
  )

  return state
}

interface UseGetSellerAuctions {
  params?: PaginationParams
  options?: UseQueryOptions<PaginationAuctionModel>
}

export const useGetSellerAuctions = (props: UseGetSellerAuctions) => {
  const { options, params } = props
  const { getSellerAuctions: config } = auctionConfig

  const state = useQuery<PaginationAuctionModel>(
    config.key(params),
    config.request,
    {
      ...options
    }
  )

  return state
}

interface UseGetBuyerBids {
  params: BuyerBidsParams
  options?: UseQueryOptions<Bid[]>
}

export const useGetBuyerBids = (props: UseGetBuyerBids) => {
  const { params, options } = props
  const { getBuyerBids: config } = auctionConfig

  const state = useQuery<Bid[]>(config.key(params), config.request, {
    ...options
  })

  return state
}

interface UseGetSellerBids {
  params: SellerBidsParams
  options?: UseQueryOptions<Bid[]>
}

export const useGetSellerBids = (props: UseGetSellerBids) => {
  const { params, options } = props
  const { getSellerBids: config } = auctionConfig

  const state = useQuery<Bid[]>(config.key(params), config.request, {
    ...options
  })

  return state
}

interface UseGetAuctionWatchLists {
  params?: PaginationParams
  options?: UseQueryOptions<PaginationWatchListModel>
}

export const useGetAuctionWatchLists = (props: UseGetAuctionWatchLists) => {
  const { options, params } = props
  const { getAuctionWatchLists: config } = auctionConfig

  const state = useQuery<PaginationWatchListModel>(
    config.key(params),
    config.request,
    {
      ...options
    }
  )

  return state
}

interface GetSellerInfiniteAuctions {
  options?: UseInfiniteQueryOptions<PaginationAuctionModel>
}

export const useGetSellerInfiniteAuctions = (
  props?: GetSellerInfiniteAuctions
) => {
  const { options } = props || {}
  const { getSellerInfiniteAuctions: config } = auctionConfig || {}

  const state = useInfiniteQuery<PaginationAuctionModel>(
    config.key,
    config.request,
    {
      ...options,
      getNextPageParam: (lastPage) => lastPage.pagy?.next,
      getPreviousPageParam: (lastPage) => lastPage.pagy?.prev
    }
  )

  return state
}

interface GetAuctionInfiniteWatchLists {
  options?: UseInfiniteQueryOptions<PaginationWatchListModel>
}

export const useGetAuctionInfiniteWatchLists = (
  props?: GetAuctionInfiniteWatchLists
) => {
  const { options } = props || {}
  const { getAuctionInfiniteWatchLists: config } = auctionConfig || {}

  const state = useInfiniteQuery<PaginationWatchListModel>(
    config.key,
    config.request,
    {
      ...options,
      getNextPageParam: (lastPage) => lastPage.pagy?.next,
      getPreviousPageParam: (lastPage) => lastPage.pagy?.prev
    }
  )

  return state
}

interface GetBuyerInfiniteAuctions {
  options?: UseInfiniteQueryOptions<PaginationAuctionModel>
}

export const useGetBuyerInfiniteAuctions = (
  props?: GetBuyerInfiniteAuctions
) => {
  const { options } = props || {}
  const { getBuyerInfiniteAuctions: config } = auctionConfig || {}

  const state = useInfiniteQuery<PaginationAuctionModel>(
    config.key,
    config.request,
    {
      ...options,
      getNextPageParam: (lastPage) => lastPage.pagy?.next,
      getPreviousPageParam: (lastPage) => lastPage.pagy?.prev
    }
  )

  return state
}
